import { useState } from "react";

import { ErrorState } from "@/app/components/legacy-components";
import {
  CardsGrid,
  DeleteSessionModal,
  DraftSessionCard,
  SessionsEmptyState,
} from "@/features/coaching/components";
import {
  useAdminSessions,
  useSessionCategory,
  useSessionStatus,
} from "@/features/coaching/hooks";
import type { AdminSession } from "@/features/coaching/models";
import { useBoolean, usePagination } from "@/shared/hooks";
import { Card, DetailCardSkeleton, Pagination } from "@/shared/ui";

export const DraftSessions = () => {
  const { category } = useSessionCategory();
  const { status } = useSessionStatus();

  const {
    page,
    pageSize,
    actions: { changePage },
  } = usePagination();

  const { data, isLoading, isSuccess, isError } = useAdminSessions({
    filter: { category, status },
    pagination: { page, pageSize },
  });
  const sessions = data?.data ?? [];
  const pagination = data?.pagination;

  const {
    value: showDeleteModal,
    setTrue: openDeleteModal,
    setFalse: closeDeleteModal,
  } = useBoolean(false);
  const [sessionToDelete, setSessionToDelete] = useState<AdminSession>();

  const handleClickDelete = (session: AdminSession) => {
    setSessionToDelete(session);
    openDeleteModal();
  };

  const handleCloseDeleteModal = () => {
    setSessionToDelete(undefined);
    closeDeleteModal();
  };

  return (
    <Card.Root className="h-full gap-4">
      {isLoading && (
        <CardsGrid>
          {Array.from({ length: 4 }).map((_, index) => (
            <DetailCardSkeleton key={`detail-card-skeleton-${index}`} />
          ))}
        </CardsGrid>
      )}

      {Boolean(isSuccess && sessions.length) && (
        <CardsGrid>
          {sessions.map((session) => (
            <DraftSessionCard
              key={session.id}
              session={session}
              onDelete={handleClickDelete}
            />
          ))}
        </CardsGrid>
      )}

      {isSuccess && !sessions.length && <SessionsEmptyState />}

      {isError && (
        <ErrorState className="justify-self-center border-transparent" />
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center border-t border-neutral-400 pt-4 md:justify-end">
          <Pagination {...pagination} onPageChange={changePage} />
        </div>
      )}

      {sessionToDelete && (
        <DeleteSessionModal
          show={Boolean(showDeleteModal && sessionToDelete)}
          onClose={handleCloseDeleteModal}
          session={sessionToDelete}
        />
      )}
    </Card.Root>
  );
};
